import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Mail, Eye, EyeOff, Trash2, Filter } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";

const SUBJECTS = {
    general: "Genel",
    advertising: "Reklam",
    press: "Basın",
    partnership: "İş Birliği",
    other: "Diğer",
};

export default function AdminContact() {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter === "unread") params.is_read = false;
            if (filter === "read") params.is_read = true;
            const { data } = await api.get("/admin/contact-requests", { params });
            setItems(data.items || []);
            setUnread(data.unread || 0);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const toggleRead = async (id, isRead) => {
        try {
            await api.put(`/admin/contact-requests/${id}/read`, null, { params: { is_read: isRead } });
            setItems((p) => p.map((it) => it.id === id ? { ...it, is_read: isRead } : it));
            setUnread((u) => Math.max(0, u + (isRead ? -1 : 1)));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Talep silinsin mi?")) return;
        try {
            await api.delete(`/admin/contact-requests/${id}`);
            setItems((p) => p.filter((it) => it.id !== id));
            toast.success("Silindi");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-contact-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy">İletişim Talepleri</h1>
                    <p className="text-sm text-deep-blue/70">
                        {unread > 0 ? <><span className="font-semibold text-data-red">{unread}</span> okunmamış mesaj.</> : "Tüm mesajlar okundu."}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-deep-blue" />
                    <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-sm rounded-md border border-slate-300 px-2 py-2" data-testid="contact-filter">
                        <option value="">Tümü</option>
                        <option value="unread">Okunmamış</option>
                        <option value="read">Okunmuş</option>
                    </select>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-100 shadow-md overflow-hidden">
                <table className="w-full text-sm" data-testid="contact-requests-table">
                    <thead className="bg-light-bg text-deep-blue">
                        <tr>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold w-10"></th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Ad Soyad</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Firma</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Konu</th>
                            <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-semibold">Tarih</th>
                            <th className="px-4 py-2.5 w-32"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Yükleniyor...</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Talep yok.</td></tr>
                        ) : items.map((it) => (
                            <tr key={it.id} className={`hover:bg-light-bg/50 ${!it.is_read ? "bg-teal/5" : ""}`}>
                                <td className="px-4 py-2.5">
                                    {!it.is_read && <span className="inline-block w-2 h-2 rounded-full bg-teal" />}
                                </td>
                                <td className="px-4 py-2.5 font-medium text-dark-navy">
                                    <div>{it.name}</div>
                                    <div className="text-xs text-deep-blue/60">{it.email}</div>
                                </td>
                                <td className="px-4 py-2.5 text-deep-blue/80">{it.company || "—"}</td>
                                <td className="px-4 py-2.5">
                                    <span className="text-xs px-2 py-0.5 rounded bg-deep-blue/10 text-deep-blue font-semibold">
                                        {SUBJECTS[it.subject] || it.subject}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 text-xs text-deep-blue/60 font-data">
                                    {it.created_at ? new Date(it.created_at).toLocaleString("tr-TR") : "—"}
                                </td>
                                <td className="px-4 py-2.5 text-right">
                                    <button onClick={() => { setOpen(it); if (!it.is_read) toggleRead(it.id, true); }} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded text-deep-blue hover:bg-light-bg" data-testid={`contact-view-${it.id}`}>
                                        <Mail className="w-3 h-3" /> Aç
                                    </button>
                                    <button onClick={() => handleDelete(it.id)} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded text-data-red hover:bg-red-50">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {open && (
                <Dialog open onOpenChange={(o) => !o && setOpen(null)}>
                    <DialogContent className="max-w-xl" data-testid="contact-detail-dialog">
                        <DialogHeader>
                            <DialogTitle>{open.name} · {SUBJECTS[open.subject] || open.subject}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 text-sm">
                            <Row k="E-posta" v={open.email} />
                            <Row k="Telefon" v={open.phone || "—"} />
                            <Row k="Firma" v={open.company || "—"} />
                            <Row k="Pozisyon" v={open.position || "—"} />
                            <Row k="Tarih" v={open.created_at ? new Date(open.created_at).toLocaleString("tr-TR") : "—"} />
                            <div>
                                <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">Mesaj</span>
                                <div className="mt-1.5 bg-light-bg rounded-md p-3 text-dark-navy whitespace-pre-wrap">{open.message}</div>
                            </div>
                            <div className="flex items-center justify-end gap-2">
                                <button
                                    onClick={() => toggleRead(open.id, !open.is_read)}
                                    className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-slate-300 hover:bg-light-bg"
                                    data-testid="contact-toggle-read"
                                >
                                    {open.is_read ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                    {open.is_read ? "Okunmadı yap" : "Okundu yap"}
                                </button>
                                <a href={`mailto:${open.email}`} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded bg-dark-navy text-white font-semibold">
                                    Yanıtla →
                                </a>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex">
            <span className="w-28 shrink-0 text-xs uppercase tracking-wider text-deep-blue/80 font-semibold">{k}</span>
            <span className="text-dark-navy">{v}</span>
        </div>
    );
}
